<template>
  <div class="wizard-page">

    <!-- HEADER -->
    <div class="wizard-header">
      <div class="wizard-progress">
        <div class="progress-left">
          <span>Step {{ progress.step }} of {{ progress.total }}</span>
          <small>{{ progress.label }}</small>
        </div>
        <div class="progress-right">{{ progressPercent }}%</div>
      </div>

      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: progressWidth }"></div>
      </div>
    </div>

    <!-- BODY -->
    <div class="wizard-body">

      <!-- STEP 1 -->
      <div v-if="progress.step === 1">

        <h1>Where do you want to get more reviews?</h1>
        <p>Select a platform to start your campaign.</p>

        <!-- ⭐ CAMPAIGN BASICS -->
        <div class="camp-basic-box">

          <label>Campaign Title</label>
          <input v-model="camp_title" placeholder="e.g. Google Review Drive" />

          <label>Description (Optional)</label>
          <input v-model="camp_desc" placeholder="Short campaign description" />

          <label v-if="campaignPlatform==='custom'">Your Custom Platform Review Page Link</label>
          <input v-if="campaignPlatform==='custom'" v-model="camp_link" placeholder="Enter Link Here" />

        </div>

        <!-- PLATFORM GRID -->
        <div class="platform-grid">
          <div v-for="platform in platforms" :key="platform.id" class="platform-card"
            :class="{ active: campaignPlatform === platform.id }" @click="selectPlatform(platform.id)">
            <!-- <div class="platform-icon" :class="platform.type"></div> -->
            <img class="platform-icon" :class="platform.type" :src="`${platform.logo}`" style="height: 3rem;" :alt="platform.title" />

            <h3>{{ platform.title }}</h3>
            <p>{{ platform.desc }}</p>

            <button v-if="platform.id!='custom'" :class="platform.status?'connected-btn':'ghost-btn'"
              @click="setConneted(platform)">
              {{ platform.status?'Connected':'Connect' }}
            </button>
          </div>
        </div>
      </div>

      <!-- STEP 2 -->
      <div v-if="progress.step === 3">

        <h1>Who do you want to invite?</h1>
        <p>Select how you'd like to bring in your leads.</p>

        <div class="invite-grid">

          <input type="file" ref="csvInput" accept=".csv, .xls, .xlsx" style="display:none"
            @change="handleFileUpload" />


          <div class="invite-card active">

           

            <h3>Upload List</h3>
            <p>Import contacts via CSV or Excel spreadsheets.</p>

            <div class="upload-actions">

              <button class="connected-btn small" @click.stop="triggerFileUpload">
                Upload File
              </button>

              <button class="ghost-btn small" @click.stop="downloadSampleCSV">
                Download Sample CSV
              </button>

            </div>
            <div v-if="invfile" class="file-preview">

              <div class="file-info">
                <span class="material-symbols-outlined">description</span>
                <span class="file-name">{{ invfile.name }}</span>
              </div>

              <button class="remove-file-btn" @click="removeFile">
                Remove
              </button>

            </div>
          </div>
          <div class="invite-card" :class="{ disabled: invfile }" style="cursor:pointer;">
            
            <h3>Single Invite</h3>
            <p>Add individual leads one by one.</p>

            <div class="upload-actions">

              <button class="connected-btn small" :disabled="invfile" @click.stop="openSingleInvite">
                Add Invitee
              </button>

              <button @click="clearList()" class="ghost-btn small">
                Clear List
              </button>

            </div>
          </div>

          <div class="invite-card">
           
            <h3>Connect Zapier</h3>
            <p>Automate lead flow from 5,000+ apps.</p>
            <div class="upload-actions">
              <button v-if="isZapier===false" @click="connectZapNow()" class="connected-btn small">
                Connect
              </button>
               <button v-if="isZapier" class="connected-btn small">
                Connected
              </button>
            </div>
          </div>

        </div>

        <div class="invite-skip-wrapper">
          <button @click="skipInviteStep()" class="footer-skip">
            I don’t have any customers yet
          </button>
        </div>

        <div v-if="invitees.length" class="invite-list">

          <div class="invite-list-header">
            <h3>Added Invitees ({{ invitees.length }})</h3>
          </div>

          <div v-for="(person, index) in invitees" :key="person.id" class="invite-row">
            <div class="invite-info">
              <strong>{{ person.name }}</strong>
              <small>{{ person.email }}</small>
              <small v-if="person.mobile">{{ person.mobile }}</small>
            </div>

            <button class="remove-invite" @click="invitees.splice(index,1)">
              ✕
            </button>
          </div>

          <div v-if="invfile" style="margin-top:10px;font-size:12px;color:#64748b;">
            Preview showing first 10 records. Remaining contacts will upload from file.
          </div>
        </div>
      </div>

      <!-- STEP 3 → DESIGN -->
      <div v-if="progress.step === 2">

        <h1>Choose Your Review Page Design</h1>
        <p>Select a layout that matches your brand style.</p>

        <div class="design-layout">

          <!-- LEFT -->
          <div class="templates-section">
            <div class="template-grid">

              <div v-for="template in templates" :key="template.id" class="template-card"
                :class="{ active: selectedTemplate === template.id }" @click="selectedTemplate = template.id">
                <img class="template-preview" :src="template.preview" />

                <div class="template-info">
                  <strong>{{ template.name }}</strong>
                  <small>{{ template.desc }}</small>
                </div>

                <button class="ghost-btn small">
                  {{ selectedTemplate === template.id ? "Selected" : "Select" }}
                </button>
              </div>

            </div>
          </div>

          <!-- RIGHT -->
          <div class="preview-section">

            <div class="preview-card">

              <!-- BRAND HEADER -->
              <div class="preview-brand">

                <div class="preview-logo">
                  <img v-if="logo" :src="logo" />
                  <span v-else>{{ businessInitials }}</span>
                </div>

                <h3>{{ businessName }}</h3>

                <p class="preview-message">
                  {{ brandMessage || "Your feedback helps us improve our service." }}
                </p>

              </div>

              <!-- TEMPLATE 1 -->
              <div v-if="selectedTemplate === 'temp1'" class="preview-body">

                <h2>Tell us how we did</h2>

                <div class="emoji-row">

                  <div class="emoji-option">
                    😊
                    <span>Great</span>
                  </div>

                  <div class="emoji-option">
                    😐
                    <span>Okay</span>
                  </div>

                  <div class="emoji-option">
                    😞
                    <span>Bad</span>
                  </div>

                </div>

              </div>

              <!-- TEMPLATE 2 -->
              <div v-if="selectedTemplate === 'temp2'" class="preview-body">

                <h2>Rate your experience</h2>

                <div class="stars">
                  ★ ★ ★ ★ ★
                </div>



              </div>

              <!-- TEMPLATE 3 -->
              <div v-if="selectedTemplate === 'temp3'" class="preview-body">

                <h2>How was your experience?</h2>

                <div class="review-btn green">
                  <span class="material-symbols-outlined">sentiment_satisfied</span>
                  Excellent
                </div>

                <div class="review-btn yellow">
                  <span class="material-symbols-outlined">sentiment_neutral</span>
                  Average
                </div>

                <div class="review-btn red">
                  <span class="material-symbols-outlined">sentiment_dissatisfied</span>
                  Needs Improvement
                </div>

              </div>

            </div>

            <div class="branding-controls" style="margin-top: 2rem;">

              <label>Business Name</label>
              <input v-model="businessName" />

              <label style="margin-top: 1rem;">Logo</label>

              <div class="logo-upload-wrapper">

                <input type="file" ref="logoInput" accept="image/*" @change="handleLogo" style="display:none" />

                <div class="logo-drop-area" @click="$refs.logoInput.click()">

                  <template v-if="logo">
                    <img :src="logo" class="logo-preview" />
                    <div class="logo-overlay">Change Logo</div>
                  </template>

                  <template v-else>
                    <div class="logo-placeholder">
                      <span>Upload Logo</span>
                      <small>PNG, JPG recommended</small>
                    </div>
                  </template>

                </div>

              </div>
              <div class="ai-toggle">

                <label class="checkbox-wrapper">
                  <input type="checkbox" v-model="aiReplyActive" />
                  <span class="checkmark"></span>
                  Activate AI Reply
                </label>

                <small>
                  Automatically generate smart responses to new reviews.
                </small>

              </div>
              <label style="margin-top: 1rem;">Review Page Message</label>
              <textarea v-model="brandMessage"
                placeholder="Write a short message that customers will see before leaving a review..."></textarea>

            </div>

          </div>

        </div>
      </div>

      <!-- STEP 4 → FOLLOWUPS -->
      <div v-if="progress.step === 4">

        <div class="step-header">
          <div>
            <h1>Automate your follow-ups</h1>
            <p>Increase conversions with smart reminders.</p>
          </div>

          <button class="add-step-btn" @click="addStep()">
            <span class="material-symbols-outlined">add</span>
            Add Step
          </button>
        </div>



        <div class="sequence-list">

          <div v-for="(item, index) in sequences" :key="item.id" class="sequence-card">
            <div class="step-indicator">{{ index + 1 }}</div>

            <div class="card-content">


              <div class="sequence-header">
                <!-- <input v-model="item.title" placeholder="Send After (Days)" type="number" class="sequence-title" /> -->
                <select v-model="item.title"
                  class="w-full border border-slate-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="" selected="">Send After(Days)</option>
                  <option v-for="d in 7" :key="d" :value="d">
                    {{ d }} Day{{ d > 1 ? 's' : '' }}
                  </option>
                </select>

                <button class="remove-step" @click="removeStep(index)">✕</button>
              </div>
              <div style="display: flex;">
                <label>Subject Line</label>
                <button v-for="v in variables" :key="v.value" type="button" class="var-btn ghost"
                  style="margin-left: 1rem;  margin-top: 3px;" @click="insertVariable(item, 'subject', v.value)">
                  {{ v.label }}
                </button>
              </div>
              <input v-model="item.subject" />
              <div style="display: flex; ">
                <label>Message Copy</label>
                <button v-for="v in variables" :key="v.value + '_msg'" type="button" class="var-btn ghost"
                  style="margin-left: 1rem; margin-top: 3px;" @click="insertVariable(item, 'message', v.value)">
                  {{ v.label }} (Msg)
                </button>
              </div>
              <textarea v-model="item.message"></textarea>

            </div>
          </div>

        </div>
      </div>

      <!-- STEP 5 → FINAL -->
      <div v-if="progress.step === 5">

        <h1>Your Review System is Ready</h1>
        <p>Your campaign has been successfully generated.</p>

        <div class="launch-grid">

          <!-- QR SECTION -->
          <div class="qr-card">
            <div class="qr-preview">
              <canvas ref="qrCanvas"></canvas>
            </div>

            <button @click="downloadQR" class="outline-btn full-btn">
              Download QR Code
            </button>
          </div>

          <!-- RIGHT SIDE -->
          <div class="url-section">

            <!-- PUBLIC LINK -->
            <h3>Public Review URL</h3>

            <div class="url-box">
              <input readonly :value="publicLink" />
              <button @click="copyLink">
                Copy Link
              </button>
            </div>

            <!-- EMBED -->
            <div class="embed-section">

              <h3>Embed on Website</h3>

              <div class="code-box">
                <code>{{ embedCode }}</code>
              </div>

              <button class="ghost-btn full-btn" @click="copyEmbed">
                Copy Embed Code
              </button>

            </div>

          </div>

        </div>

      </div>

    </div>

    <!-- FOOTER -->
    <div class="wizard-footer">
      <button @click="backNow()" class="footer-back">Back</button>

      <button @click="nextNow(0)" class="footer-next">
        {{ progress.step === 5?"View Campaigns":progress.step === progress.total-2 ? "Create Campaign" : "Continue" }}
      </button>
    </div>
    <!-- SINGLE INVITE MODAL -->
    <div v-if="showSingleInvite" class="modal-overlay">

      <div class="modal-card">

        <div class="modal-header">
          <h3>Add Campaign Invite</h3>
          <button @click="closeSingleInvite">✕</button>
        </div>

        <div class="modal-body">

          <label>Name</label>
          <input v-model="singleInvite.camp_inv_name" />

          <label>Email</label>
          <input v-model="singleInvite.camp_inv_email" />

          <label>Mobile (optional)</label>
          <input v-model="singleInvite.camp_inv_mob" />

        </div>

        <div class="modal-footer">
          <button class="ghost-btn" @click="closeSingleInvite">Cancel</button>
          <button class="connected-btn" @click="saveSingleInvite">
            Save Invite
          </button>
        </div>

      </div>

    </div>
  </div>
</template>
<script>
  import { useRoute } from 'vue-router'
  import QRCode from "qrcode";
  import { mainBaseUrl } from "@/apis/appconst";
  import { createCampaign, getCampById } from "../apis/campaignapis";
  import { useToast } from 'vue-toastification';
  import * as XLSX from "xlsx";
  import { getIntegration } from "@/apis/intigrationapis";
  import { apiBaseUrl } from "@/apis/appconst";
  import {
    getQrUrl
  } from "../apis/qrapis";

  export default {
    name: "PlatformWizard",
    mounted() {
      this.toast = useToast();
      const route = useRoute();
      const campId = route.query.campId;
     
      if (campId && campId != null && campId != undefined) {
        this.initCamp(campId);
      }

      const tmp = sessionStorage.getItem("ldata") || "";
      if (tmp && tmp.length > 0) {
        this.user_id = JSON.parse(tmp).user_id;
        this.loadIntegration("Google", "google");
        this.loadIntegration("Facebook", "fb");
        this.loadIntegration("Zapier Auth Code", "zapier");
      }
    },
    data() {
      return {
        variables: [
          { label: "First Name", value: "{{first_name}}" }
        ],
        showZapModal: false,
        zapUrl: "",
        zapTimer: null,
        aiReplyActive: false,
        brandMessage: "",
        user_id: "",
        invitees: [],
        isGoogle: false,
        isFacebook: false,
        isZapier: false,
        invfile: null,
        toast: null,
        showSingleInvite: false,
        camp_id: "",
        camp_title: "",
        camp_desc: "",
        camp_link: "",
        camp_cat: "review",
        camp_platform: "",
        camp_templete: "modern",
        camp_color: "",
        singleInvite: {
          camp_inv_name: "",
          camp_inv_email: "",
          camp_inv_mob: ""
        },

        /* ================= UI STATE ================= */

        selectedTemplate: "temp1",
        businessName: "The Artisan Bakery",
        logo: "/images/logo1.png",
        logo1: "/images/logo1.png",
        campaignPlatform: "custom",

        /* ================= FOLLOWUPS ================= */

        sequences: [
          {
            id: 1,
            title: "",
            subject: "How was your experience with us?",
            message: "Hey, we'd love to hear your thoughts."
          }
        ],

        /* ================= PROGRESS ================= */

        progress: {
          step: 1,
          total: 5,
          label: "Platform Selection"
        },

        /* ================= DESIGN TEMPLATES ================= */

        templates: [
          {
            id: "temp1",
            name: "Template 1",
            desc: "Minimal, distraction-free experience",
            preview: "/images/temp1.jpg"
          },
          {
            id: "temp2",
            name: "Template 2",
            desc: "Optimized for maximum reviews",
            preview: "/images/temp2.jpg"
          },
          {
            id: "temp3",
            name: "Template 3",
            desc: "Designed for QR & mobile visitors",
            preview: "/images/temp3.jpg"
          }
        ],

        /* ================= PLATFORMS ================= */

       platforms: [
        {
          id: "google",
          title: "Google Profile",
          desc: "Connect your existing Google Business profile.",
          type: "google",
          logo: "/images/google.png",
          buttonText: "Connect",
          buttonClass: "ghost-btn",
          status: false
        },
        {
          id: "facebook",
          title: "Facebook Page",
          desc: "Get more reviews on Facebook.",
          type: "facebook",
          logo: "/images/facebook.png",
          buttonText: "Connect",
          buttonClass: "ghost-btn",
          status: false
        },
        {
          id: "custom",
          title: "Custom Link",
          desc: "Request reviews anywhere.",
          type: "custom",
          logo: "/images/logo.png",
          buttonText: "Connect",
          buttonClass: "outline-btn",
          status: false
        }
      ]
      };
    },

    /* ================= COMPUTED ================= */

    computed: {
      publicLink() {
        return mainBaseUrl + `review/${this.camp_id}`;
      },

      embedCode() {

        var url = mainBaseUrl + `review/${this.camp_id}`;

        return `<iframe 
                src="${url}" 
                width="100%" 
                height="600" 
                frameborder="0" 
                scrolling="no"
                style="border:0;border-radius:12px;overflow:hidden;"
              ></iframe>`;
      },
      progressPercent() {
        return Math.round((this.progress.step / this.progress.total) * 100);
      },

      progressWidth() {
        return this.progressPercent + "%";
      },

      businessInitials() {
        return this.businessName
          .split(" ")
          .map(w => w[0])
          .join("")
          .substring(0, 2)
          .toUpperCase();
      }
    },

    watch: {
      "progress.step"(val) {
        if (val === 5) {
          this.$nextTick(() => {
            this.generateQR();
          });
        }
      },

    },
    /* ================= METHODS ================= */

    methods: {
      async loadIntegration(title, type) {

        try {

          const res = await getIntegration(title, type, this.user_id);

          if (!res.status) {
            if (type === "google") {
              this.isGoogle = false;
              this.platforms[0].status = false;
            }
            else if (type === "zapier") {
              this.isZapier = false;
            }
            else {
              this.isFacebook = false;
              this.platforms[1].status = false;
            }
            return false;
          }
          else if (res.int_dt != null) {
            if (type === "google") {
              this.isGoogle = true;
              this.platforms[0].status = true;
            }
            else if (type === "zapier") {
              this.isZapier = true;
            }
            else {
              this.isFacebook = true;
              this.platforms[1].status = true;
            }
            return true;
          }
        }
        catch (err) {
          console.error(err);
        }
        if (type === "google") {
              this.isGoogle = false;
            }
            else if (type === "zapier") {
              this.isZapier = false;
            }
            else {
              this.isFacebook = false;
            }
        return false;
      },
      removeFile() {
        if (this.$refs.csvInput) {
          this.$refs.csvInput.value = null;   // reset previous file
          this.$refs.csvInput.value = "";
        }
        this.invfile = null
        this.invitees = []
      },
      async initCamp(campId) {

        try {

          const res = await getCampById(campId);

          if (!res.status) return;

          const campDt = res.tmp;
          const invs = res.invs || [];
          const follows = res.follows || [];

          /* ================= BASIC INFO ================= */

          this.camp_id = campDt.camp_id;
          this.camp_title = campDt.camp_title || "";
          this.camp_desc = campDt.camp_desc || "";
          this.camp_cat = campDt.camp_cat || "review";
          this.campaignPlatform = campDt.camp_platform || "google";
          this.camp_link = campDt.camp_link || "";
          this.selectedTemplate = campDt.camp_templete || "temp1";
          this.camp_color = campDt.camp_color || "";
          this.brandMessage = campDt.camp_fav || "";

          if (campDt.camp_logo && campDt.camp_logo.length > 0) {
            this.logo = campDt.camp_logo;
          }

          /* ================= INVITEES ================= */

          this.invitees = invs.map(inv => ({
            id: inv.camp_inv_id,
            name: inv.camp_inv_name,
            email: inv.camp_inv_email,
            mobile: inv.camp_inv_mob,
            status: inv.camp_inv_status,
            ai_reply: inv.camp_inv_ai_replay
          }));

          /* ================= FOLLOWUPS ================= */

          if (follows.length > 0) {

            this.sequences = follows.map(f => ({
              id: f.camp_follow_id,
              title: f.camp_follow_title || "",
              subject: f.camp_follow_subject || "",
              message: f.camp_follow_body || ""
            }));

          } else {
            this.sequences = [];
          }

          /* ================= STEP POSITION ================= */

          // When editing, start from step 1
          this.progress.step = 1;
          this.progress.label = "Platform Selection";

          this.toast.success("Campaign loaded");



        } catch (err) {
          console.error(err);
          this.toast.error("Failed to load campaign");
        }
      },
      insertVariable(item, field, variable) {
        item[field] += " " + variable;
      },
      skipInviteStep() {
        this.nextNow(1);
        // this.toast.info("You can invite customers later.");
      },
      clearList() {
        this.invitees = [];
      },
      setConneted(platform) {
        if (!platform.status) {
          if (platform.id === 'google') {
            window.location.href = apiBaseUrl + "gmb/connect?uid=" + this.user_id;
          }
          else {
            window.location.href = apiBaseUrl + "fb/connect?uid=" + this.user_id;
          }
        }
      },
        async downloadQR() {

  const url = mainBaseUrl + "review/" + this.camp_id;

  const res = await getQrUrl(url, "camp", this.camp_id, this.camp_user);
  if (!res.status) return;

  const qrUrl = apiBaseUrl + res.link;

  const qrCanvas = document.createElement("canvas");

  QRCode.toCanvas(qrCanvas, qrUrl, {
    width: 600,
    margin: 1,
    errorCorrectionLevel:"H",
    color: {
      dark: "#197fe6",
      light: "#ffffff"
    }
  }, (error) => {

    if (error) {
      console.error(error);
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = 900;
    canvas.height = 1200;

    const ctx = canvas.getContext("2d");

    /* ---------- background ---------- */

    ctx.fillStyle = "#f8fafc";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    /* ---------- card ---------- */

    ctx.fillStyle = "#ffffff";
    ctx.shadowColor = "rgba(0,0,0,0.12)";
    ctx.shadowBlur = 30;
    ctx.fillRect(120, 120, 660, 920);

    ctx.shadowBlur = 0;

    /* ---------- header ---------- */

    ctx.fillStyle = "#197fe6";
    ctx.fillRect(120, 120, 660, 120);

    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 42px Arial";
    ctx.textAlign = "center";
    ctx.fillText("REVIEWSFIX", 450, 195);

    /* ---------- QR ---------- */

    ctx.drawImage(qrCanvas, 240, 300, 420, 420);

    /* ---------- logo center ---------- */

    const logo = new Image();
    logo.src = "/images/logo.png";

    logo.onload = () => {

      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(450, 510, 70, 0, Math.PI * 2);
      ctx.fill();

      ctx.drawImage(logo, 400, 460, 100, 100);

      /* ---------- stars ---------- */

      ctx.fillStyle = "#fbbf24";
      ctx.font = "34px Arial";
      ctx.fillText("★★★★★", 450, 770);

      /* ---------- CTA ---------- */

      ctx.fillStyle = "#111";
      ctx.font = "bold 36px Arial";
      ctx.fillText("Scan to Leave a Review", 450, 840);

      ctx.fillStyle = "#64748b";
      ctx.font = "24px Arial";
      ctx.fillText("Your feedback helps us grow", 450, 890);

      /* ---------- footer brand ---------- */

      ctx.fillStyle = "#197fe6";
      ctx.font = "bold 26px Arial";
      ctx.fillText("Powered by Reviewsfix", 450, 980);

      /* ---------- download ---------- */

      const link = document.createElement("a");
      link.download = `reviewsfix-qr-${this.camp_id}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();

      if (this.$toast) {
        this.$toast.success("Branded QR downloaded");
      }

    };

  });

},
      async generateQR() {

  if (!this.publicLink || !this.$refs.qrCanvas) return;

  try {

    const canvas = this.$refs.qrCanvas;
    const ctx = canvas.getContext("2d");

    const size = 180;   // fits inside qr-preview (200px box)

    canvas.width = size;
    canvas.height = size;

    await QRCode.toCanvas(canvas, this.publicLink, {
      width: size,
      margin: 0,
      color: {
        dark: "#197fe6",
        light: "#ffffff"
      },
      errorCorrectionLevel: "H"
    });

    const logo = new Image();
    logo.src = "/images/logo.png";

    logo.onload = () => {

      const logoSize = size * 0.25;
      const x = (size - logoSize) / 2;
      const y = (size - logoSize) / 2;

      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, logoSize / 2 + 4, 0, Math.PI * 2);
      ctx.fill();

      ctx.drawImage(logo, x, y, logoSize, logoSize);

    };

  } catch (err) {
    console.error("QR generation failed", err);
  }

},
      connectZapNow() {

        let user_id = "";
        const tmp = sessionStorage.getItem("ldata") || "";

        if (tmp) {
          user_id = JSON.parse(tmp).user_id;
        }

        const url = apiBaseUrl+"zapier/connect?rtype=new_camp&user_id="+user_id;
        window.location.href = url;
      },
      downloadSampleCSV() {

        const csvContent =
          "Name,Email,Mobile\n" +
          "John Doe,john@example.com,(000) 000 0000\n" +
          "Jane Smith,jane@example.com,(000) 000 0000\n";

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "sample_invite_template.csv";
        link.click();

        URL.revokeObjectURL(url);
      },
      closeSingleInvite() {
        this.showSingleInvite = false;
        this.singleInvite = { name: "", email: "", phone: "" };
      },
      saveSingleInvite() {

        if (!this.singleInvite.camp_inv_name.trim()) {
          this.toast.error("Name required");
          return;
        }

        if (!this.singleInvite.camp_inv_email.trim()) {
          this.toast.error("Email required");
          return;
        }

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(this.singleInvite.camp_inv_email)) {
          this.toast.error("Invalid email");
          return;
        }

        // Push to local invite list
        this.invitees.push({
          id: Date.now(),
          name: this.singleInvite.camp_inv_name,
          email: this.singleInvite.camp_inv_email,
          mobile: this.singleInvite.camp_inv_mob
        });

        this.toast.success("Invite added");

        this.closeSingleInvite();
      },
      openSingleInvite() {

        this.singleInvite = {
          camp_inv_name: "",
          camp_inv_email: "",
          camp_inv_mob: ""
        };

        this.showSingleInvite = true;
      },
      triggerFileUpload() {
        this.$refs.csvInput.click();
      },

      handleFileUpload(e) {

        const file = e.target.files[0];
        if (!file) return;

        this.invfile = file;   // send file to backend
        this.invitees = [];    // reset preview list

        const reader = new FileReader();

        reader.onload = (evt) => {

          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet);

          const limitedRows = rows.slice(0, 1000);   // max 1000

          const previewRows = limitedRows.slice(0, 10); // UI preview only

          previewRows.forEach(row => {

            this.invitees.push({
              id: Date.now() + Math.random(),
              name: row.Name || row.name || "",
              email: row.Email || row.email || "",
              mobile: row.Mobile || row.mobile || ""
            });

          });

          const remaining = limitedRows.length - previewRows.length;

          if (remaining > 0) {
            this.toast.info(
              `${previewRows.length} shown. ${remaining} more will upload with file`
            );
          }

        };

        reader.readAsArrayBuffer(file);

      },
      copyLink() {
        navigator.clipboard.writeText(this.publicLink);
        this.toast.success("Link copied");
      },

      copyEmbed() {
        navigator.clipboard.writeText(this.embedCode);
        this.toast.success("Embed code copied");
      },
      validateStep() {

        this.errors = {
          camp_title: "",
          campaignPlatform: "",
          sequences: ""
        };

        if (this.progress.step === 1) {

          if (!this.camp_title.trim()) {
            this.errors.camp_title = "Campaign title required";

            this.toast.error("Campaign title required");
            return false;
          }

          if (!this.campaignPlatform) {
            this.errors.campaignPlatform = "Select a platform";
            this.toast.error("Please select a platform");
            return false;
          }

          if (this.campaignPlatform === "custom") {
            if (!this.camp_link.trim()) {
              this.errors.camp_link = "Campaign link required";

              this.toast.error("Campaign link required");
              return false;
            }

          }
        }

        if (this.progress.step === 3) {



          if (this.invitees.length <= 0) {

            this.toast.error("Add Some Invitees");
            return false;
          }
        }

        if (this.progress.step === 4) {

          const invalid = this.sequences.some(s =>
            !s.subject.trim() || !s.message.trim()
          );

          if (invalid) {
            this.errors.sequences = "Complete follow-up fields";

            this.toast.error("Follow-up fields incomplete");
            return false;
          }
        }

        return true;
      },
      /* ---------- PLATFORM SELECT ---------- */

      selectPlatform(id) {
        // alert(id+"");
        if (id === 'google') {
          if (!this.platforms[0].status) {
            this.toast.error("Connect Google My Business");
            return;
          }
        }
        else if (id === 'facebook') {
          if (!this.platforms[1].status) {
            this.toast.error("Connect Facebook");
            return;
          }
        }
        this.campaignPlatform = id;
      },

      /* ---------- FOLLOWUP STEPS ---------- */

      addStep() {
        const newStep = {
          id: Date.now(),
          title: "Follow-up Reminder",
          subject: "",
          message: ""
        };

        this.sequences.push(newStep);
      },

      removeStep(index) {
        this.sequences.splice(index, 1);
      },

      /* ---------- LOGO ---------- */

      handleLogo(e) {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
          this.toast.error("Only image files allowed");
          return;
        }

        this.logo1 = file;
        this.logo = URL.createObjectURL(file);
      },

      /* ---------- NAVIGATION ---------- */

      async nextNow(rtype) {
        if (rtype === 0 && this.validateStep() == false) {
          return;
        }
        /* ✅ FINAL STEP → CREATE CAMPAIGN */
        if (this.progress.step === this.progress.total - 1 || rtype === 1) {

          try {
            var user_id = "";
            const tmp = sessionStorage.getItem("ldata") || "";
            if (tmp && tmp.length > 0) {
              user_id = JSON.parse(tmp).user_id;
            }

            const res = await createCampaign(
              user_id,
              this.camp_id,
              this.camp_title,
              this.camp_desc,
              this.camp_cat,
              this.camp_link,
              this.campaignPlatform,
              this.selectedTemplate,
              this.camp_color,
              this.logo1,
              this.sequences,
              this.invfile ? [] : this.invitees,
              this.invfile,
              this.aiReplyActive,
              this.brandMessage
            );

            if (res.status) {
              this.toast.success(res.success || "Campaign created");
              this.camp_id = res.camp_id;   // ⭐ VERY IMPORTANT
              // window.location.href = "/camplain-list";
              this.progress.step = 5;
            } else {
              this.toast.error(res.success || "Campaign creation failed");
            }

          } catch (err) {
            console.error(err);
            this.toast.error("Server error");
          }

          return;
        }

        /* ✅ NORMAL STEP FLOW */
        if (this.progress.step < this.progress.total) {

          this.progress.step++;

          if (this.progress.step === 3) this.progress.label = "Lead Selection";
          if (this.progress.step === 2) this.progress.label = "Design Selection";
          if (this.progress.step === 4) this.progress.label = "Follow-up Automation";
          if (this.progress.step === 5) this.progress.label = "Launch";
        }
        if (this.progress.step === 5) {
          window.location.href = "/camplain-list";
        }
      },

      backNow() {
        if (this.progress.step > 1) {
          this.progress.step--;
        }
      }
    }
  };
</script>
<style scoped>
  /* ================= PAGE ================= */
  .invite-skip-wrapper {
    margin-top: 30px;
    display: flex;
    justify-content: center;
  }

  .invite-skip-btn {
    height: 40px;
    padding: 0 28px;
    border-radius: 12px;
    font-weight: 700;
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    transition: 0.2s;
  }

  .invite-skip-btn:hover {
    border-color: #197fe6;
    color: #197fe6;
  }

  .wizard-page {
    background: #f6f7f8;
    min-height: 100vh;
    padding: 30px;
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  /* ================= HEADER ================= */

  .wizard-header {
    background: white;
    border-radius: 18px;
    padding: 18px 24px;
    border: 1px solid #e2e8f0;
  }

  .wizard-progress {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .progress-left span {
    font-weight: 700;
    color: #197fe6;
  }

  .progress-left small {
    display: block;
    font-size: 12px;
    color: #64748b;
    margin-top: 2px;
  }

  .progress-right {
    font-weight: 800;
    font-size: 14px;
  }

  .progress-bar {
    margin-top: 12px;
    height: 6px;
    background: #f1f5f9;
    border-radius: 20px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: #197fe6;
    transition: 0.3s;
  }

  /* ================= BODY ================= */

  .wizard-body {
    background: white;
    border-radius: 18px;
    border: 1px solid #e2e8f0;
    padding: 32px 36px;
    flex: 1;
  }

  h1 {
    font-size: 26px;
    font-weight: 800;
    margin-bottom: 6px;
    color: #0f172a;
  }

  p {
    font-size: 14px;
    color: #64748b;
    margin-bottom: 24px;
  }

  /* ================= GRIDS ================= */

  .platform-grid,
  .invite-grid,
  .template-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 16px;
  }

  /* ================= CARDS ================= */

  .platform-card,
  .invite-card,
  .template-card {
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 18px;
    background: white;
    cursor: pointer;
    transition: 0.2s;
    position: relative;
  }

  .platform-card:hover,
  .invite-card:hover,
  .template-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.05);
  }

  .platform-card.active,
  .invite-card.active,
  .template-card.active {
    border: 2px solid #197fe6;
  }

  /* ================= ICONS ================= */

  .platform-icon,
  .invite-icon {
    height: 1rem;
    border-radius: 12px;
    margin-bottom: 10px;
  }

  .google {
    background: #eef2ff;
  }

  .facebook {
    background: #eff6ff;
  }

  .custom {
    background: #fff7ed;
  }

  .upload {
    background: #eff6ff;
  }

  .manual {
    background: #ecfdf5;
  }

  .zapier {
    background: #fef3c7;
  }

  /* ================= TEXT ================= */

  .platform-card h3,
  .invite-card h3,
  .template-card strong {
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 4px;
    color: #0f172a;
  }

  .platform-card p,
  .invite-card p,
  .template-card small {
    font-size: 12.5px;
    color: #64748b;
  }

  /* ================= BUTTONS ================= */

  .connected-btn,
  .ghost-btn,
  .outline-btn {
    margin-top: 12px;
    width: 100%;
    height: 36px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: 0.15s;
  }

  .connected-btn {
    background: #197fe6;
    color: white;
    border: none;
  }

  .connected-btn:hover {
    background: #0f6ad6;
  }

  .ghost-btn {
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
  }

  .ghost-btn:hover {
    background: #e2e8f0;
  }

  .outline-btn {
    background: white;
    border: 1px dashed #cbd5f5;
  }

  .outline-btn:hover {
    border-color: #197fe6;
    color: #197fe6;
  }

  /* ================= DESIGN LAYOUT ================= */

  .design-layout {
    display: grid;
    grid-template-columns: 1.2fr 0.8fr;
    gap: 26px;
  }

  .templates-section,
  .preview-card,
  .branding-controls {
    border: 1px solid #e2e8f0;
    border-radius: 18px;
    padding: 18px;
    background: white;
  }

  .preview-section {
    position: sticky;
    top: 20px;
  }

  .preview-ui {
    margin-top: 16px;
    border-radius: 14px;
    padding: 18px;
    background: #f8fafc;
  }

  .fake-stars {
    font-size: 18px;
    color: #fbbf24;
    margin-bottom: 10px;
  }

  .fake-text {
    height: 10px;
    background: #e2e8f0;
    border-radius: 6px;
    margin-bottom: 8px;
  }

  .fake-button {
    height: 32px;
    background: #197fe6;
    border-radius: 10px;
  }

  .ui-bold {
    background: linear-gradient(135deg, #197fe6, #60a5fa);
    color: white;
  }

  .ui-compact {
    background: #0f172a;
    color: white;
  }

  /* ================= FOLLOWUPS ================= */

  .sequence-card {
    display: flex;
    gap: 14px;
    margin-top: 12px;
  }

  .step-indicator {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #197fe6;
    color: white;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .card-content {
    flex: 1;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 16px;
  }

  .sequence-header {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
  }

  .sequence-title {
    border: none;
    font-weight: 700;
    font-size: 13px;
  }

  .remove-step {
    margin-left: auto;
    border: none;
    background: none;
    cursor: pointer;
    color: #94a3b8;
  }

  .remove-step:hover {
    color: #ef4444;
  }

  .add-step-btn {
    height: 36px;
    padding: 0 16px;
    border-radius: 12px;
    background: #0f172a;
    color: white;
    border: none;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }

  /* ================= INPUTS ================= */

  label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #64748b;
    margin-top: 10px;
    display: block;
  }

  input,
  textarea {
    width: 100%;
    height: 36px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 0 10px;
    font-size: 13px;
    margin-top: 4px;
  }

  textarea {
    height: 70px;
    resize: none;
    padding-top: 8px;
  }

  /* ================= FOOTER ================= */

  .wizard-footer {
    background: white;
    border-radius: 18px;
    border: 1px solid #e2e8f0;
    padding: 16px 22px;
    display: flex;
    align-items: center;
  }

  .footer-back {
    border: none;
    background: none;
    font-weight: 700;
    color: #64748b;
    cursor: pointer;
  }

  .footer-next {
    margin-left: auto;
    height: 38px;
    padding: 0 22px;
    border-radius: 12px;
    border: none;
    background: #197fe6;
    color: white;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
  }

  .footer-next:hover {
    background: #0f6ad6;
  }

  /* ================= RESPONSIVE ================= */

  @media (max-width: 900px) {

    .wizard-page {
      padding: 16px;
    }

    .wizard-body {
      padding: 22px;
    }

    .design-layout {
      grid-template-columns: 1fr;
    }

    .footer-next,
    .footer-back {
      width: 100%;
    }

  }

  /* ================= CAMPAIGN BASIC BOX ================= */

  .camp-basic-box {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 16px;
    margin-bottom: 22px;
  }

  .camp-basic-box label:first-child {
    margin-top: 0;
  }

  /* ================= TEMPLATE PREVIEW ================= */

  .template-preview {
    height: 10rem;
    border-radius: 12px;
    margin-bottom: 12px;
    background: #f1f5f9;
    margin-left: 2rem;
    border: 1px solid #f1f5f9;
    position: relative;
    overflow: hidden;
  }

  /* Fake preview visual styles */

  .preview-modern {
    background: linear-gradient(135deg, #eef2ff, #f8fafc);
  }

  .preview-bold {
    background: linear-gradient(135deg, #197fe6, #60a5fa);
  }

  .preview-compact {
    background: linear-gradient(135deg, #0f172a, #334155);
  }

  /* ================= TEMPLATE INFO ================= */

  .template-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 10px;
  }

  /* ================= PREVIEW LOGO ================= */

  .preview-logo {
    width: auto;
    height: 3rem;
    border-radius: 14px;
    background: #ffffff;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 14px;
    overflow: hidden;
  }

  .preview-logo img {
    width: auto;
    height: 4rem;
    object-fit: cover;
  }

  /* ================= SMART STOP ================= */

  .smart-stop {
    margin-bottom: 18px;
    box-shadow: 0 10px 25px rgba(25, 127, 230, 0.08);
  }

  /* ================= QR / FINAL SCREEN ================= */

  .launch-grid {
    display: grid;
    grid-template-columns: 260px 1fr;
    gap: 22px;
    margin-top: 12px;
  }

  .qr-card {
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 18px;
    background: white;
    text-align: center;
  }

  .qr-preview {
    height: 200px;
    border-radius: 14px;
    background: #f8fafc;
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed #cbd5f5;
  }

  .url-section h3 {
    font-size: 15px;
    font-weight: 800;
    margin-bottom: 10px;
    color: #0f172a;
  }

  .url-box {
    display: flex;
    gap: 10px;
  }

  .url-box input {
    flex: 1;
    background: #f8fafc;
  }

  .url-box button {
    height: 36px;
    padding: 0 18px;
    border-radius: 10px;
    border: none;
    background: #197fe6;
    color: white;
    font-size: 12.5px;
    font-weight: 700;
    cursor: pointer;
  }

  .url-box button:hover {
    background: #0f6ad6;
  }

  /* ================= RESPONSIVE FIX ================= */

  @media (max-width: 900px) {

    .launch-grid {
      grid-template-columns: 1fr;
    }

  }

  .step-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    /* ⭐ THIS FIXES VERTICAL MISALIGNMENT */
    gap: 12px;
    margin-bottom: 18px;
  }

  /* Prevent text block from stretching weirdly */
  .step-header>div {
    display: flex;
    flex-direction: column;
  }

  /* Stable Add Step button */
  .add-step-btn {
    height: 38px;
    padding: 0 18px;
    border-radius: 12px;
    background: #0f172a;
    color: white;
    border: none;
    font-size: 13px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    white-space: nowrap;
    /* ⭐ prevents wrapping jump */
    flex-shrink: 0;
    /* ⭐ prevents compression */
  }

  /* Icon size consistency */
  .add-step-btn span {
    font-size: 18px;
  }

  .embed-section {
    margin-top: 22px;
  }

  .code-box {
    margin-top: 10px;
    background: #0f172a;
    color: #e2e8f0;
    padding: 14px;
    border-radius: 12px;
    font-size: 11px;
    position: relative;
    overflow-x: auto;
  }

  .code-box code {
    white-space: pre-line;
    font-family: monospace;
  }

  /* ================= MODAL OVERLAY ================= */

  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.55);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
    animation: fadeIn 0.2s ease;
  }

  /* ================= MODAL CARD ================= */

  .modal-card {
    width: 460px;
    max-width: 95%;
    background: #ffffff;
    border-radius: 20px;
    padding: 24px;
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.15);
    animation: scaleIn 0.2s ease;
    display: flex;
    flex-direction: column;
  }

  /* ================= MODAL HEADER ================= */

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
  }

  .modal-header h3 {
    font-size: 18px;
    font-weight: 800;
    color: #0f172a;
    margin: 0;
  }

  .modal-header button {
    border: none;
    background: none;
    font-size: 18px;
    cursor: pointer;
    color: #64748b;
    transition: 0.2s;
  }

  .modal-header button:hover {
    color: #ef4444;
  }

  /* ================= MODAL BODY ================= */

  .modal-body {
    display: flex;
    flex-direction: column;
  }

  .modal-body label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #64748b;
    margin-top: 12px;
  }

  .modal-body input {
    width: 100%;
    height: 38px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 0 12px;
    font-size: 13px;
    margin-top: 6px;
    transition: 0.2s;
  }

  .modal-body input:focus {
    outline: none;
    border-color: #197fe6;
    box-shadow: 0 0 0 3px rgba(25, 127, 230, 0.15);
  }

  /* ================= MODAL FOOTER ================= */

  .modal-footer {
    margin-top: 22px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  .modal-footer .ghost-btn,
  .modal-footer .connected-btn {
    height: 38px;
    padding: 0 18px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 700;
  }

  /* ================= ANIMATIONS ================= */

  @keyframes fadeIn {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }

    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  /* ================= RESPONSIVE ================= */

  @media (max-width: 500px) {
    .modal-card {
      padding: 18px;
    }

    .modal-header h3 {
      font-size: 16px;
    }
  }

  /* ================= INVITEE LIST ================= */

  .invite-list {
    margin-top: 30px;
    padding-top: 18px;
    border-top: 1px solid #e2e8f0;
  }

  .invite-list-header h3 {
    font-size: 15px;
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 12px;
  }

  .invite-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    background: #f8fafc;
    margin-bottom: 10px;
    transition: 0.2s;
  }

  .invite-row:hover {
    background: #eef2ff;
  }

  .invite-info strong {
    display: block;
    font-size: 13px;
    color: #0f172a;
  }

  .invite-info small {
    display: block;
    font-size: 12px;
    color: #64748b;
  }

  .remove-invite {
    border: none;
    background: none;
    font-size: 16px;
    cursor: pointer;
    color: #94a3b8;
    transition: 0.2s;
  }

  .remove-invite:hover {
    color: #ef4444;
  }

  .small {
    height: 32px !important;
    font-size: 12px !important;
    margin-top: 10px;
  }

  /* ================= FINAL BUTTON FIXES ================= */

  .full-btn {
    width: 100%;
    height: 40px;
    margin-top: 14px;
    border-radius: 12px;
    font-weight: 700;
  }

  /* QR button */
  .qr-card .outline-btn {
    border: 1px solid #197fe6;
    color: #197fe6;
  }

  .qr-card .outline-btn:hover {
    background: #197fe6;
    color: white;
  }

  /* Copy link button */
  .url-box button {
    min-width: 110px;
    height: 40px;
    border-radius: 10px;
    font-weight: 700;
  }

  /* Embed section spacing */
  .embed-section {
    margin-top: 24px;
  }

  .upload-actions {
    display: flex;
    gap: 10px;
    margin-top: 12px;
    flex-wrap: wrap;
  }

  .upload-actions .small {
    flex: 1;
  }

  .footer-skip {
    margin-left: auto;
    margin-right: 10px;
    height: 38px;
    padding: 0 18px;
    border-radius: 12px;
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    color: #64748b;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: 0.2s;
    margin: 2rem;
  }

  .footer-skip:hover {
    border-color: #197fe6;
    color: #197fe6;
  }

  .logo-upload-wrapper {
    margin-top: 6px;
  }

  .logo-drop-area {
    width: 100%;
    height: 120px;
    border-radius: 14px;
    border: 2px dashed #cbd5e1;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: 0.2s;
  }

  .logo-drop-area:hover {
    border-color: #ffffff;
    background: #eef2ff;
  }

  .logo-placeholder {
    text-align: center;
  }

  .logo-placeholder span {
    font-weight: 700;
    font-size: 13px;
    display: block;
  }

  .logo-placeholder small {
    font-size: 11px;
    color: #64748b;
  }

  .logo-preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .logo-overlay {
    position: absolute;
    bottom: 0;
    width: 100%;
    background: rgba(15, 23, 42, 0.6);
    color: white;
    text-align: center;
    padding: 6px 0;
    font-size: 12px;
    font-weight: 600;
  }

  .ai-toggle {
    margin-top: 18px;
    padding: 14px;
    border-radius: 12px;
    background: #f8fafc;
  }

  .checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
    color: #0f172a;
  }

  .checkbox-wrapper input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  .ai-toggle small {
    display: block;
    margin-top: 6px;
    font-size: 11px;
    color: #64748b;
  }

  /* FIX CHECKBOX STYLE */
  input[type="checkbox"] {
    width: 18px !important;
    height: 18px !important;
    margin: 0;
    padding: 0;
    border-radius: 4px;
    border-color: #000;
    accent-color: #197fe6;
    cursor: pointer;
  }

  .variable-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin-bottom: 10px;
  }

  .variable-bar span {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    margin-right: 6px;
  }

  .var-btn {
    height: 26px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid #197fe6;
    background: #eef2ff;
    color: #197fe6;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: 0.2s;
  }

  .var-btn:hover {
    background: #197fe6;
    color: white;
  }

  .var-btn.ghost {
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    color: #334155;
  }

  .var-btn.ghost:hover {
    border-color: #197fe6;
    color: #197fe6;
  }

  .file-preview {
    margin-top: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f8fafc;
  }

  .file-info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
  }

  .file-name {
    color: #0f172a;
  }

  .remove-file-btn {
    border: none;
    background: #fee2e2;
    color: #ef4444;
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
  }

  .invite-card.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .preview-brand {
    text-align: center;
    margin-bottom: 20px;
  }

  .preview-logo {
    width: auto;
    height: 4rem;
    border-radius: 16px;
    background: #ffffff;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 18px;
    margin: auto;
    margin-bottom: 10px;
    overflow: hidden;
  }

  .preview-logo img {
    width: auto;
    height: 4rem;
    object-fit: cover;
  }

  .preview-message {
    font-size: 12px;
    color: #64748b;
    margin-top: 6px;
  }

  .preview-body {
    text-align: center;
  }

  .preview-body h2 {
    font-size: 16px;
    font-weight: 800;
    margin-bottom: 12px;
  }

  .emoji-row {
    display: flex;
    justify-content: center;
    gap: 24px;
    margin-top: 10px;
  }

  .emoji-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 26px;
    cursor: pointer;
  }

  .emoji-option span {
    font-size: 12px;
    margin-top: 4px;
  }

  .stars {
    font-size: 26px;
    color: #fbbf24;
    margin-bottom: 14px;
  }

  .fake-form input,
  .fake-form textarea {
    width: 100%;
    margin-top: 8px;
    height: 34px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    padding: 6px;
    font-size: 12px;
  }

  .fake-form textarea {
    height: 60px;
  }

  .btn-green {
    width: 100%;
    margin-top: 10px;
    height: 36px;
    background: #22c55e;
    color: white;
    border: none;
    border-radius: 10px;
  }

  .btn-yellow {
    width: 100%;
    margin-top: 10px;
    height: 36px;
    background: #f59e0b;
    color: white;
    border: none;
    border-radius: 10px;
  }

  .btn-red {
    width: 100%;
    margin-top: 10px;
    height: 36px;
    background: #ef4444;
    color: white;
    border: none;
    border-radius: 10px;
  }

  .review-btn {
    width: 100%;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 16px;
    font-weight: 700;
    font-size: 14px;
    color: white;
    margin-top: 10px;
    cursor: pointer;
  }

  .review-btn span {
    font-size: 20px;
  }

  /* COLORS */

  .review-btn.green {
    background: #22c55e;
  }

  .review-btn.yellow {
    background: #f59e0b;
  }

  .review-btn.red {
    background: #ef4444;
  }
  .platform-icon{
  height:1rem;
  background-size:contain;
  background-repeat:no-repeat;
  background-position:center;
}

</style>